/**
 * Case study archive — progetti 04–09 (non in homepage scroller).
 * Immagini in /public/projects/{slug}/
 */
export const archiveProjects = [
  {
    slug: 'rain-alley',
    path: '/rain-alley',
    index: '04',
    title: 'Rain Alley',
    stripLabel: 'Unreal Engine · Environment Design',
    studioLine: 'Personal Project · 2025',
    category: 'Unreal Engine · Environment Design',
    year: '2025',
    tagline:
      'A fully modeled urban environment built in Unreal Engine 5: rain systems, Lumen GI and ray-traced reflections throughout.',
    heroImage: '/projects/rain-alley/rain-alley-frame-01.webp',
    heroLayout: 'overlay',
    introVariant: 'spacious',
    introHeader: {
      index: '004',
      stripLabel: 'Unreal Engine · Environment Design',
      studioLine: 'Personal Project · 2025',
      tagline:
        'A fully modeled urban environment built in Unreal Engine 5: rain systems, Lumen GI and ray-traced reflections throughout.',
    },
    metaOrder: ['client', 'role', 'engine', 'year', 'deliverables', 'process'],
    next: { slug: 'wraith', path: '/wraith', title: 'Wraith' },
    nextLabel: '— Next project',
    meta: {
      client: 'Personal Project',
      role: '3D Modeling · Environment Design · Lighting',
      engine: 'Unreal Engine 5',
      year: '2025',
      deliverables: '8 Cinematic Renders · Full Environment',
      process: 'Modeling · PBR Texturing · Lumen GI · Rain Particle System · Nanite',
    },
    intro: [
      'A complete urban environment built entirely in Unreal Engine 5: geometry, materials, lighting and VFX systems produced from scratch.',
      'The brief was self-imposed: build a cinematic alley scene production-ready as a game level, a film backdrop or a standalone visual piece.',
      'Every asset was modeled and textured directly: wet asphalt with PBR reflective surface, police cruiser with damage maps, neon signage with emissive materials and a full volumetric rain particle system.',
    ],
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'shot',
        src: '/projects/rain-alley/rain-alley-frame-02.webp',
        alt: 'Wide establishing — volumetric rain · Lumen GI',
        caption: 'SHOT 01 — Wide establishing · Volumetric rain · Lumen GI',
        variant: 'bleed',
      },
      {
        type: 'process',
        layout: 'narrow',
        title: 'Process note',
        paragraphs: [
          'The core technical challenge was water: wet surface reflections, rain particle density and puddle dynamics required precise layering of Lumen Global Illumination and ray-traced reflections to achieve cinematic output quality.',
          'Lumen GI was used for indirect light bounce between surfaces: neon sign emission, street lamp spill and ambient fog interaction all calculated in real time. Ray-traced reflections on the wet asphalt and vehicle body were tuned per camera position to maximize visual impact across all eight angles.',
          'Eight camera positions were selected to cover the full spatial range: macro surface detail, mid-range vehicle shots and wide establishing views with full fog depth.',
        ],
      },
      {
        type: 'grid',
        rows: [
          {
            left: {
              src: '/projects/rain-alley/rain-alley-frame-03.webp',
              alt: 'Wide establishing — ambient fog depth',
              caption: 'SHOT 02 — Wide establishing · Ambient fog depth',
            },
            right: {
              src: '/projects/rain-alley/rain-alley-frame-04.webp',
              alt: 'Hero vehicle — Lumen ray-traced reflections',
              caption: 'SHOT 03 — Hero vehicle · Lumen ray-traced reflections',
            },
          },
          {
            left: {
              src: '/projects/rain-alley/rain-alley-frame-05.webp',
              alt: 'Vehicle approach — neon emissive · wet surface',
              caption: 'SHOT 04 — Vehicle approach · Neon emissive · Wet surface',
            },
            right: {
              src: '/projects/rain-alley/rain-alley-frame-06.webp',
              alt: 'Alley depth — fog system · ambient occlusion',
              caption: 'SHOT 05 — Alley depth · Fog system · Ambient occlusion',
            },
          },
          {
            left: {
              src: '/projects/rain-alley/rain-alley-frame-07.webp',
              alt: 'Side approach — vehicle detail · PBR surface',
              caption: 'SHOT 06 — Side approach · Vehicle detail · PBR surface',
            },
            right: {
              src: '/projects/rain-alley/rain-alley-frame-08.webp',
              alt: 'Isometric overview — full environment layout',
              caption: 'SHOT 07 — Isometric overview · Full environment layout',
            },
          },
        ],
      },
    ],
    discipline: 'Unreal Engine · Environment Design',
    blurb:
      'UE5 rain alley with PBR environment, Lumen GI, ray-traced reflections and volumetric rain across eight cinematic angles.',
    media: '/projects/rain-alley/rain-alley-frame-01.webp',
  },
  {
    slug: 'wraith',
    path: '/wraith',
    index: '05',
    title: 'Wraith',
    stripLabel: 'Character Design · AI × 3D',
    studioLine: 'Psyop Studio · New York · 2026',
    category: 'Character Design · AI × 3D',
    year: '2026',
    tagline:
      'A character concept developed for Psyop Studio: hard surface 3D base combined with AI-generated organic layer for an undisclosed VFX project.',
    heroImage: '/projects/wraith/wraith-frame-00.webp',
    heroLayout: 'overlay',
    introVariant: 'spacious',
    introHeader: {
      index: '005',
      stripLabel: 'Character Design · AI × 3D',
      studioLine: 'Psyop Studio · New York · 2026',
      tagline:
        'A character concept developed for Psyop Studio: hard surface 3D base combined with AI-generated organic layer for an undisclosed VFX project.',
    },
    next: { slug: 'shelter', path: '/shelter', title: 'Shelter' },
    nextLabel: '— Next project',
    meta: {
      client: 'Psyop Studio, New York',
      role: 'Character Design · AI Art Direction · 3D Modeling',
      format: 'Character Concept · VFX Pre-production',
      year: '2026',
      deliverables: 'Character Sheet · Hero Renders · Multi-angle Set',
      process: 'Hard Surface Modeling · AI Generation · Compositing · Multi-pass Refinement',
    },
    intro: [
      'Commissioned by Psyop Studio for an undisclosed VFX project requiring a creature character concept for pre-production reference.',
      'The brief required a hybrid mechanical-organic form: hard surface exoskeleton structure combined with AI-generated translucent organic layer, skin system and tendrils built around it.',
      'The 3D base defined proportions, joint structure and surface geometry. AI generation handled the organic matter (veiling, tendrils and skin system), composited around the structural base per angle.',
    ],
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'process',
        layout: 'narrow',
        title: 'Process note',
        paragraphs: [
          'The primary technical constraint was character consistency across a full multi-angle set using a hybrid 3D/AI pipeline.',
          'The 3D structural base was built first: hard surface geometry, panel detail and joint articulation defined upfront to anchor proportions and silhouette across every frame.',
          'AI generation was applied over the structural reference per angle, with organic layer placement, translucency and tendril distribution controlled through iterative art direction at each generation pass.',
          'Lighting setup was locked to a neutral high-key studio with diffused fill, to isolate the character form and maintain material readability across the full character sheet.',
        ],
      },
      {
        type: 'grid',
        rows: [
          {
            left: {
              src: '/projects/wraith/wraith-frame-02.webp',
              alt: 'Rear view — full body structure · hard surface detail',
              caption: 'REAR VIEW — Full body structure · Hard surface detail',
            },
            right: {
              src: '/projects/wraith/wraith-frame-03.webp',
              alt: 'Three-quarter view — upper body · organic layer distribution',
              caption: 'THREE-QUARTER VIEW — Upper body · Organic layer distribution',
            },
          },
          {
            left: {
              src: '/projects/wraith/wraith-frame-04.webp',
              alt: 'Rear view — full body · organic layer · tendrils',
              caption: 'REAR VIEW — Full body · Organic layer · Tendrils',
            },
            right: {
              src: '/projects/wraith/wraith-frame-05.webp',
              alt: 'Top down view — frontal approach · silhouette read',
              caption: 'TOP DOWN VIEW — Frontal approach · Silhouette read',
            },
          },
        ],
      },
      {
        type: 'full',
        variant: 'centered',
        src: '/projects/wraith/wraith-frame-06.webp',
        alt: 'Front view — full body silhouette · character sheet reference',
        caption: 'FRONT VIEW — Full body silhouette · Character sheet reference',
      },
    ],
    discipline: 'Character Design · AI × 3D',
    blurb:
      'Psyop Studio creature concept: hard surface 3D base, AI organic layer and locked studio lighting across a multi-angle character sheet.',
    media: '/projects/wraith/wraith-frame-00.webp',
  },
  {
    slug: 'shelter',
    path: '/shelter',
    index: '06',
    title: 'Shelter',
    stripLabel: 'Unreal Engine · Environment Design',
    studioLine: 'SG Game Indie Studios · Europe · 2025',
    category: 'Unreal Engine · Environment Design',
    year: '2025',
    tagline:
      'An abandoned industrial interior built in Unreal Engine 5 for SG Game Indie Studios: full asset pipeline, volumetric lighting and PBR materials.',
    heroImage: '/projects/shelter/shelter-frame-01.webp',
    heroLayout: 'overlay',
    introVariant: 'spacious',
    introHeader: {
      index: '006',
      stripLabel: 'Unreal Engine · Environment Design',
      studioLine: 'SG Game Indie Studios · Europe · 2025',
      tagline:
        'An abandoned industrial interior built in Unreal Engine 5 for SG Game Indie Studios: full asset pipeline, volumetric lighting and PBR materials.',
    },
    metaOrder: ['client', 'role', 'engine', 'year', 'deliverables', 'process'],
    next: { slug: 'bar-scene', path: '/bar-scene', title: 'SWIPE NIGHT' },
    nextLabel: '— Next project',
    meta: {
      client: 'SG Game Indie Studios · Europe',
      role: 'Environment Design · Lighting · Atmosphere',
      engine: 'Unreal Engine 5',
      year: '2025',
      deliverables: 'Cinematic Renders · Interior Environment',
      process: 'Modular Asset Modeling · PBR Texturing · Lumen GI · Volumetric Fog',
    },
    intro: [
      'Developed for SG Game Indie Studios, an independent game development studio based in Europe.',
      'The brief required a fully realized abandoned industrial interior built entirely in Unreal Engine 5: every asset modeled, textured and lit in-engine, with no external compositing.',
      'Wet concrete floors with PBR reflective surface, fluorescent overhead fixtures, graffiti-covered walls with decal layering and scattered props, all built as modular assets and dressed directly in the scene.',
    ],
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'shot',
        src: '/projects/shelter/shelter-frame-02.webp',
        alt: 'Wide establishing — volumetric fog system · Lumen GI',
        caption: 'SHOT 01 — Wide establishing · Volumetric fog system · Lumen GI',
        variant: 'bleed',
      },
      {
        type: 'process',
        layout: 'narrow',
        title: 'Process note',
        paragraphs: [
          'The lighting system used three distinct sources layered for depth: cold fluorescent overhead as key, warm practical fill from secondary fixtures and teal ambient for color separation across the background geometry.',
          'Volumetric fog was positioned as a structural element, not an atmospheric effect. Density and falloff were tuned to obscure the far end of the room and extend implied depth beyond the actual geometry bounds.',
          'Lumen Global Illumination handled indirect bounce between surfaces, with emissive materials on fluorescent tubes contributing to ambient fill across the wet floor reflections throughout.',
        ],
      },
      {
        type: 'grid',
        rows: [
          {
            left: {
              src: '/projects/shelter/shelter-frame-03.webp',
              alt: 'Corridor view — cold fluorescent key · fog depth',
              caption: 'SHOT 02 — Corridor view · Cold fluorescent key · Fog depth',
            },
            right: {
              src: '/projects/shelter/shelter-frame-04.webp',
              alt: 'Lateral view — graffiti decal · teal ambient',
              caption: 'SHOT 03 — Lateral view · Graffiti decal · Teal ambient',
            },
          },
          {
            left: {
              src: '/projects/shelter/shelter-frame-05.webp',
              alt: 'Lateral angle — warm practical fill · wet floor',
              caption: 'SHOT 04 — Lateral angle · Warm practical fill · Wet floor',
            },
            right: {
              src: '/projects/shelter/shelter-frame-06.webp',
              alt: 'Deep corridor — fog density · shelf detail',
              caption: 'SHOT 05 — Deep corridor · Fog density · Shelf detail',
            },
          },
        ],
      },
      {
        type: 'full',
        variant: 'centered',
        src: '/projects/shelter/shelter-frame-04.webp',
        alt: 'Detail view — graffiti wall · water reflection · PBR surface',
        caption: 'SHOT 06 — Detail view · Graffiti wall · Water reflection · PBR surface',
      },
    ],
    discipline: 'Unreal Engine · Environment Design',
    blurb:
      'UE5 industrial interior for SG Game Indie Studios: modular assets, Lumen GI, volumetric fog and layered fluorescent lighting.',
    media: '/projects/shelter/shelter-frame-01.webp',
  },
  {
    slug: 'bar-scene',
    path: '/bar-scene',
    index: '07',
    title: 'SWIPE NIGHT',
    stripLabel: 'Hybrid AI · Real Shot · Cinematography',
    studioLine: 'Varsha Studios · 2025',
    category: 'Hybrid AI · Real Shot · Cinematography',
    year: '2025',
    tagline:
      'Visual development for a pilot series: cinematic sequences produced through a hybrid real shot and AI pipeline.',
    heroImage: '/projects/bar-scene/bar-scene-frame-01.webp',
    heroLayout: 'overlay',
    introVariant: 'spacious',
    introHeader: {
      index: '007',
      stripLabel: 'Hybrid AI · Real Shot · Cinematography',
      studioLine: 'Varsha Studios · 2025',
      tagline:
        'Visual development for a pilot series: cinematic sequences produced through a hybrid real shot and AI pipeline.',
    },
    next: { slug: 'dojaka', path: '/dojaka', title: 'Dojaka' },
    nextLabel: '— Next project',
    meta: {
      client: 'Varsha Studios',
      role: 'AI Art Direction · Hybrid Production',
      format: 'Pilot Series · Visual Development',
      year: '2025',
      deliverables: '11 Cinematic Shots · Scene Sequences',
      process: 'Real Shot Integration · AI Background Plates · Color Direction',
    },
    intro: [
      'Commissioned by Varsha Studios to develop visual sequences and hero shots for a pilot series.',
      'The brief required a consistent cinematic language across eleven frames: controlled lighting, defined color temperature and a specific atmospheric register maintained throughout.',
      'The workflow was hybrid: real shot subjects were composited into AI-generated background plates, with lighting and atmosphere built and iterated per frame.',
    ],
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'shot',
        src: '/projects/bar-scene/bar-scene-frame-02.webp',
        alt: 'Hero close up — bar scene with warm practical light',
        caption: 'SHOT 01 — Hero close up · Bar scene · Warm practical light',
        variant: 'bleed',
      },
      {
        type: 'process',
        layout: 'narrow',
        title: 'Process note',
        paragraphs: [
          'Each frame was treated as a directed shot within a defined sequence. Color temperature, subject placement and focal length consistency were established upfront and maintained across all eleven frames through iterative art direction at every generation pass.',
          'Real shot subjects were composited into AI-generated background plates, with practical light sources and ambient fill built and matched per frame.',
          'Shot continuity across the full sequence was the primary technical constraint, ensuring the hybrid origin is not visible in the final output.',
        ],
      },
      {
        type: 'grid',
        rows: [
          {
            left: {
              src: '/projects/bar-scene/bar-scene-frame-03.webp',
              alt: 'Corridor — practical wall light',
              caption: 'SEQUENCE 01 — Corridor · Practical wall light',
            },
            right: {
              src: '/projects/bar-scene/bar-scene-frame-04.webp',
              alt: 'Confrontation — shallow depth of field',
              caption: 'SEQUENCE 02 — Confrontation · Shallow depth of field',
            },
          },
          {
            left: {
              src: '/projects/bar-scene/bar-scene-frame-05.webp',
              alt: 'Doorframe — warm tungsten close framing',
              caption: 'SEQUENCE 03 — Doorframe · Warm tungsten · Close framing',
            },
            right: {
              src: '/projects/bar-scene/bar-scene-frame-06.webp',
              alt: 'Bedroom wide shot — rain exterior',
              caption: 'SEQUENCE 04 — Bedroom · Wide shot · Rain exterior',
            },
          },
          {
            left: {
              src: '/projects/bar-scene/bar-scene-frame-07.webp',
              alt: 'Bedroom lateral view — ambient light',
              caption: 'SEQUENCE 05 — Bedroom · Lateral view · Ambient light',
            },
            right: {
              src: '/projects/bar-scene/bar-scene-frame-08.webp',
              alt: 'Bedroom close framing — window backlight',
              caption: 'SEQUENCE 06 — Bedroom · Close framing · Window backlight',
            },
          },
        ],
      },
      {
        type: 'full',
        variant: 'centered',
        src: '/projects/bar-scene/bar-scene-frame-09.webp',
        alt: 'Bedroom overhead — final scene',
        caption: 'SEQUENCE 07 — Bedroom · Overhead · Final scene',
      },
    ],
    discipline: 'Hybrid AI · Real Shot · Cinematography',
    blurb:
      'Hybrid real shot and AI visual development for a Varsha Studios pilot: eleven cinematic shots with consistent art direction across the sequence.',
    media: '/projects/bar-scene/bar-scene-frame-01.webp',
  },
  {
    slug: 'dojaka',
    path: '/dojaka',
    index: '08',
    title: 'Dojaka',
    stripLabel: 'AI Art Direction · Fashion · Film',
    studioLine: 'Poster Dojaka · USA · 2025',
    category: 'AI Art Direction · Fashion · Film',
    year: '2025',
    tagline:
      'Visual sequences and campaign clips developed for Poster Dojaka: cinematic stills and motion produced through a hybrid real shot and AI pipeline.',
    heroImage: '/projects/dojaka/dojaka-frame-01.webp',
    heroLayout: 'overlay',
    introVariant: 'spacious',
    introHeader: {
      index: '008',
      stripLabel: 'AI Art Direction · Fashion · Film',
      studioLine: 'Poster Dojaka · USA · 2025',
      tagline:
        'Visual sequences and campaign clips developed for Poster Dojaka: cinematic stills and motion produced through a hybrid real shot and AI pipeline.',
    },
    next: { slug: 'klm-board', path: '/klm-board', title: 'KLM BOARD' },
    nextLabel: '— Next project',
    meta: {
      client: 'Poster Dojaka',
      role: 'AI Art Direction · Visual Development · Film',
      format: 'Fashion Campaign · Video Clip',
      year: '2025',
      deliverables: '16 Hero Shots · 2 Video Clips · Sequences',
      process: 'Subject Isolation · AI Background Plates · Color Grading · After Effects · Premiere Pro',
    },
    intro: [
      'Commissioned by Poster Dojaka to develop the visual language for a fashion campaign: sixteen stills and two video clips produced as a unified deliverable set.',
      'The brief required cinematic sequences and hero shots across multiple environments and lighting conditions, maintaining consistent subject identity and brand presence throughout.',
      'Real shot subjects were isolated and composited into AI-generated background plates, with environment, lighting and color temperature defined per sequence and locked across the full set.',
    ],
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'shot',
        src: '/projects/dojaka/dojaka-frame-02.webp',
        alt: 'Hero frame — top down · environmental plate · warm tungsten',
        caption: 'SHOT 01 — Hero frame · Top down · Environmental plate · Warm tungsten',
        variant: 'bleed',
      },
      {
        type: 'process',
        layout: 'narrow',
        title: 'Process note',
        paragraphs: [
          'Sixteen stills and two video clips produced as a unified visual sequence. Each frame was built to function both as a standalone campaign image and as part of a defined sequence structure.',
          'Visual language, color temperature, focal length and subject-to-environment relationship were established upfront and maintained across the full deliverable set through iterative art direction at every generation pass.',
          'The two video clips were assembled from AI-generated frame sequences, edited and composited in Adobe Premiere Pro and After Effects, with color grading, transition timing and motion pacing handled in post to match campaign tone and brand direction.',
        ],
      },
      {
        type: 'grid',
        rows: [
          {
            left: {
              src: '/projects/dojaka/dojaka-frame-03.webp',
              alt: 'Subject — water environment · lateral framing',
              caption: 'SEQUENCE 01 — Subject · Water environment · Lateral framing',
            },
            right: {
              src: '/projects/dojaka/dojaka-frame-04.webp',
              alt: 'Macro close up — shallow depth of field · warm fill',
              caption: 'SEQUENCE 02 — Macro close up · Shallow DOF · Warm fill',
            },
          },
          {
            left: {
              src: '/projects/dojaka/dojaka-frame-05.webp',
              alt: 'Subject — architectural plate · golden hour',
              caption: 'SEQUENCE 03 — Subject · Architectural plate · Golden hour',
            },
            right: {
              src: '/projects/dojaka/dojaka-frame-06.webp',
              alt: 'Subject — architectural plate · cool ambient',
              caption: 'SEQUENCE 04 — Subject · Architectural plate · Cool ambient',
            },
          },
          {
            left: {
              src: '/projects/dojaka/dojaka-frame-07.webp',
              alt: 'Subject — urban plate · backlight',
              caption: 'SEQUENCE 05 — Subject · Urban plate · Backlight',
            },
            right: {
              src: '/projects/dojaka/dojaka-frame-08.webp',
              alt: 'Subject — corrugated surface · split light',
              caption: 'SEQUENCE 06 — Subject · Corrugated surface · Split light',
            },
          },
          {
            left: {
              src: '/projects/dojaka/dojaka-frame-09.webp',
              alt: 'Detail frame — garment · shallow depth of field',
              caption: 'SEQUENCE 07 — Detail frame · Garment · Shallow DOF',
            },
            right: {
              src: '/projects/dojaka/dojaka-frame-10.webp',
              alt: 'Subject — corrugated surface · close framing',
              caption: 'SEQUENCE 08 — SUBJECT · CORRUGATED SURFACE · CLOSE FRAMING',
            },
          },
          {
            left: {
              src: '/projects/dojaka/dojaka-frame-11.webp',
              alt: 'Wildlife plate — natural environment · golden hour',
              caption: 'SEQUENCE 09 — WILDLIFE PLATE · NATURAL ENVIRONMENT · GOLDEN HOUR',
            },
            right: {
              src: '/projects/dojaka/dojaka-frame-12.webp',
              alt: 'Aerial view — architectural plate · desert environment',
              caption: 'SEQUENCE 10 — AERIAL VIEW · ARCHITECTURAL PLATE · DESERT ENVIRONMENT',
            },
          },
        ],
      },
      {
        type: 'video',
        src: '/projects/dojaka/dojaka-clip-01.mp4',
        poster: '/projects/dojaka/dojaka-frame-03.webp',
        caption: 'CLIP 01 — CAMPAIGN SEQUENCE · MOTION EDIT · AFTER EFFECTS · PREMIERE PRO',
      },
      {
        type: 'full',
        variant: 'centered',
        src: '/projects/dojaka/dojaka-frame-13.webp',
        alt: 'Campaign sequence — motion edit · desert environment plate',
        caption: 'CLIP 02 — CAMPAIGN SEQUENCE · MOTION EDIT · DESERT ENVIRONMENT PLATE',
      },
    ],
    discipline: 'AI Art Direction · Fashion · Film',
    blurb:
      'Poster Dojaka fashion campaign: sixteen hybrid stills and two clips with locked art direction, color grade and Premiere/After Effects post.',
    media: '/projects/dojaka/dojaka-frame-01.webp',
  },
  {
    slug: 'klm-board',
    path: '/klm-board',
    index: '009',
    title: 'KLM BOARD',
    stripLabel: 'INFORMATION DESIGN · GOVERNANCE',
    studioLine: 'KLM ROYAL DUTCH AIRLINES · 2025',
    category: 'Information Design · Governance',
    year: '2025',
    tagline:
      "Executive governance dashboard designed for one of Europe's largest airlines: data visualization and reporting boards built for internal strategic use.",
    heroImage: '/projects/klm-board/klm-board-frame-01.webp',
    heroLayout: 'text',
    introVariant: 'spacious',
    metaOrder: ['client', 'role', 'format', 'year', 'deliverables', 'note'],
    next: { slug: 'dior-board', path: '/dior-board', title: 'DIOR BOARD' },
    nextLabel: '— Next project',
    meta: {
      client: 'KLM Royal Dutch Airlines',
      role: 'Information Design · Data Visualization',
      format: 'Governance Dashboard · Reporting Board',
      year: '2025',
      deliverables: 'Executive Dashboard · KPI Visualization',
      note: 'Data obscured under NDA',
    },
    intro: [
      'Governance boards designed to present sales data and strategic KPIs to executive leadership. Clarity and hierarchy as the primary design objective.',
    ],
    introNote: '— Figures and data obscured for confidentiality',
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'document',
        src: '/projects/klm-board/klm-board-frame-01.webp',
        alt: 'Air France–KLM — OBM transition program cover',
      },
      {
        type: 'document',
        src: '/projects/klm-board/klm-board-frame-02.webp',
        alt: 'Governance structure — transformation program overview',
      },
      {
        type: 'document',
        src: '/projects/klm-board/klm-board-frame-03.webp',
        alt: 'Aeonvis PMO transformation team — roles and responsibilities',
      },
    ],
    discipline: 'Information Design · Governance',
    blurb:
      'Executive governance dashboard for KLM: KPI visualization and reporting boards for internal strategic use.',
    media: '/projects/klm-board/klm-board-frame-01.webp',
  },
  {
    slug: 'dior-board',
    path: '/dior-board',
    index: '010',
    title: 'DIOR BOARD',
    stripLabel: 'INFORMATION DESIGN · GOVERNANCE',
    studioLine: 'DIOR · 2025',
    category: 'Information Design · Governance',
    year: '2025',
    tagline:
      'Governance reporting boards designed for Dior: structured data visualization for executive presentation.',
    heroImage: '/projects/dior-board/dior-board-frame-01.webp',
    heroLayout: 'text',
    introVariant: 'spacious',
    metaOrder: ['client', 'role', 'format', 'year', 'deliverables', 'note'],
    next: { slug: 'mismo-studio', path: '/mismo-studio', title: 'MISMO STUDIO' },
    nextLabel: '— Next project',
    meta: {
      client: 'Dior',
      role: 'Information Design · Data Visualization',
      format: 'Governance Dashboard · Reporting Board',
      year: '2025',
      deliverables: 'Executive Dashboard · KPI Visualization',
      note: 'Data obscured under NDA',
    },
    intro: [
      "Brand governance boards for one of the world's most recognized luxury houses: precision, hierarchy and visual clarity at executive level.",
    ],
    introNote: '— Figures and data obscured for confidentiality',
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'document',
        src: '/projects/dior-board/dior-board-frame-01.webp',
        alt: 'Dior — program cover',
      },
      {
        type: 'document',
        src: '/projects/dior-board/dior-board-frame-02.webp',
        alt: 'Program approach — integration, architecture and security',
      },
    ],
    discipline: 'Information Design · Governance',
    blurb:
      'Governance reporting boards for Dior: executive KPI visualization with data obscured under NDA.',
    media: '/projects/dior-board/dior-board-frame-01.webp',
  },
  {
    slug: 'mismo-studio',
    path: '/mismo-studio',
    index: '011',
    title: 'MISMO STUDIO',
    stripLabel: 'UI DESIGN · ANIMATION · WEB DEVELOPMENT',
    studioLine: 'MISMO STUDIO · VERONA · 2025',
    category: 'UI Design · Web Development',
    year: '2025',
    tagline:
      'Full website design and development for a Verona-based creative studio: clean system, coherent typography and animated interactions throughout.',
    heroImage: '/projects/mismo-studio/mismo-studio-frame-01.webp',
    heroLayout: 'overlay',
    mediaPresentation: 'screenshot',
    introVariant: 'spacious',
    metaOrder: ['client', 'role', 'format', 'year', 'deliverables'],
    next: { slug: 'mescalina', path: '/mescalina', title: 'MESCALINA' },
    nextLabel: '— Next project',
    meta: {
      client: 'Mismo Studio · Verona',
      role: 'UI Design · Animation · Web Development',
      format: 'Full Website · Responsive',
      year: '2025',
      deliverables: 'Full Website · Animated Interactions',
    },
    intro: [
      "A complete website for a graphic and branding studio. The design system mirrors the studio's own identity principles: clear, coherent, built to last.",
    ],
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'video',
        src: '/projects/mismo-studio/mismo-studio-clip-01.mp4',
        poster: '/projects/mismo-studio/mismo-studio-frame-01.webp',
        caption: 'CLIP 01 — Homepage · News ticker & editorial grid',
      },
      {
        type: 'full',
        variant: 'centered',
        fit: 'contain',
        src: '/projects/mismo-studio/mismo-studio-frame-02.webp',
        alt: 'Works index — brand identity grid',
        caption: 'Works index · Project gallery',
      },
      {
        type: 'full',
        variant: 'centered',
        fit: 'contain',
        src: '/projects/mismo-studio/mismo-studio-frame-04.webp',
        alt: 'Selected works — packaging and brand detail',
        caption: 'Selected works · Brand applications',
      },
      {
        type: 'video',
        src: '/projects/mismo-studio/mismo-studio-clip-02.mp4',
        poster: '/projects/mismo-studio/mismo-studio-frame-03.webp',
        caption: 'CLIP 02 — BRAND SYSTEM · LOGO SHOWCASE · INTERACTIVE VIEW',
      },
      {
        type: 'full',
        variant: 'centered',
        fit: 'contain',
        src: '/projects/mismo-studio/mismo-studio-frame-03.webp',
        alt: 'Brand system — logo sheet and client overview',
        caption: 'BRAND SYSTEM · LOGO SHEET · CLIENT OVERVIEW',
      },
    ],
    discipline: 'UI Design · Animation · Web Development',
    blurb:
      'Full website for Mismo Studio, Verona: design system, motion and responsive build.',
    media: '/projects/mismo-studio/mismo-studio-frame-01.webp',
  },
  {
    slug: 'mescalina',
    path: '/mescalina',
    index: '012',
    title: 'MESCALINA',
    stripLabel: 'UI DESIGN · ANIMATION · WEB DEVELOPMENT',
    studioLine: 'MESCALINA CREATIVE LAB · VERONA · 2025',
    category: 'UI Design · Web Development',
    year: '2025',
    tagline:
      'Website design and development for a Verona-based creative and communication agency: bold visual identity translated into an animated digital experience.',
    heroImage: '/projects/mescalina/mescalina-frame-01.webp',
    heroLayout: 'overlay',
    mediaPresentation: 'screenshot',
    introVariant: 'spacious',
    metaOrder: ['client', 'role', 'format', 'year', 'deliverables'],
    next: {
      slug: 'work',
      path: '/work',
      title: '← Back to all work',
      showArrow: false,
      variant: 'archive-end',
    },
    nextLabel: '— ARCHIVE COMPLETE',
    meta: {
      client: 'Mescalina Creative Lab · Verona',
      role: 'UI Design · Animation · Web Development',
      format: 'Full Website · Responsive',
      year: '2025',
      deliverables: 'Full Website · Animated Interactions · 3D Elements',
    },
    intro: [
      'A communication agency with a strong brand voice. The design translates their identity into a bold, direct digital presence with custom animated elements and an immediately recognizable visual system.',
    ],
    processInBlocks: true,
    tech: [],
    blocks: [
      {
        type: 'video',
        src: '/projects/mescalina/mescalina-clip-01.mp4',
        poster: '/projects/mescalina/mescalina-frame-01.webp',
        caption: 'CLIP 01 — Homepage · Motion & interactions',
      },
      {
        type: 'grid',
        rows: [
          {
            left: {
              src: '/projects/mescalina/mescalina-frame-03.webp',
              alt: 'Selected works — DVC Music device mockup',
              compact: true,
              caption: 'SELECTED WORKS · DVC MUSIC · DEVICE MOCKUP',
            },
            right: {
              src: '/projects/mescalina/mescalina-frame-04.webp',
              alt: 'Selected works — Advbeca brand manual',
              compact: true,
              caption: 'SELECTED WORKS · ADVBECA · BRAND MANUAL',
            },
          },
        ],
      },
      {
        type: 'full',
        variant: 'centered',
        fit: 'contain',
        src: '/projects/mescalina/mescalina-frame-05.webp',
        alt: 'About page — team, philosophy and contact',
        caption: 'About · Team & philosophy',
      },
      {
        type: 'video',
        src: '/projects/mescalina/mescalina-clip-02.mp4',
        poster: '/projects/mescalina/mescalina-frame-03.webp',
        caption: 'CLIP 02 — Portfolio · Scroll & layout',
      },
    ],
    discipline: 'UI Design · Animation · Web Development',
    blurb:
      'Website for Mescalina Creative Lab: bold identity, 3D elements and animated interactions.',
    media: '/projects/mescalina/mescalina-frame-01.webp',
  },
];
